import type { TransferSearchViewProps } from './index.types';
import { useTransferContactSearch } from 'Modules/softphone/hooks/useTransferContactSearch';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type {
  TransferSearchResult,
  TransferTarget,
} from 'Modules/softphone/types/transfer.types';
import React, { useCallback } from 'react';
import { TransferHeader } from '../TransferHeader';
import { TransferSearchInput } from './TransferSearchInput';
import { TransferSearchList } from './TransferSearchList';

/**
 * TransferSearchView Component
 *
 * Renders the search interface for finding transfer destinations.
 * Displays a search input, held call context bar, contact results,
 * and a direct-dial option when the query matches a phone number pattern.
 */
export const TransferSearchView = ({
  onSelectContact,
  onSelectNumber,
  variant = 'popup',
  testID,
}: TransferSearchViewProps): React.ReactElement => {
  const componentTestId = testID ? `${testID}-search` : 'search';

  const { searchTerm, cancelTransfer } = useSoftphoneStore((state) => ({
    searchTerm: state.searchTerm,
    cancelTransfer: state.cancelTransfer,
  }));

  const {
    results,
    isLoading,
    showDirectNumberOption,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTransferContactSearch(searchTerm);

  const handleSelectContact = useCallback(
    (contact: TransferSearchResult) => {
      const target: TransferTarget = {
        type: 'contact',
        displayName: contact.displayName,
        contactId: contact.id,
        sourceId: contact.sourceId,
        contactType: contact.contactType,
        phoneNumbers: contact.phoneNumbers,
        hasVoicemail: contact.hasVoicemail,
        voicemailInboxId: contact.voicemailInboxId,
      };
      onSelectContact(target);
    },
    [onSelectContact]
  );

  const handleSelectDirectNumber = useCallback(() => {
    onSelectNumber(searchTerm.trim());
  }, [onSelectNumber, searchTerm]);

  return (
    <div
      className="flex flex-col h-full gap-[16px] mb-[5px]"
      data-testid={componentTestId}
      role="search"
      aria-label="Search transfer destination"
    >
      <TransferHeader onBack={cancelTransfer} testID={componentTestId} />

      <TransferSearchInput testID={componentTestId} />

      <TransferSearchList
        results={results}
        isLoading={isLoading}
        showDirectNumberOption={showDirectNumberOption}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        searchTerm={searchTerm}
        onSelectContact={handleSelectContact}
        onSelectDirectNumber={handleSelectDirectNumber}
        variant={variant}
        testID={componentTestId}
      />
    </div>
  );
};

TransferSearchView.displayName = 'TransferSearchView';

